import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCode, FiExternalLink, FiGithub, FiSearch } from "react-icons/fi";
import { projectsAPI, getImageUrl } from "../services/api";

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTech, setSelectedTech] = useState("all");

  const [portfolioRef, portfolioInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await projectsAPI.getProjects();
        const data = Array.isArray(response.data) ? response.data : [];
        setProjects(data);
        setFilteredProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const allTechnologies = [
    "all",
    ...new Set(
      (Array.isArray(projects) ? projects : []).flatMap(
        (project) => project.technologies || []
      )
    ),
  ];

  useEffect(() => {
    let filtered = Array.isArray(projects) ? [...projects] : [];

    if (searchTerm) {
      filtered = filtered.filter(
        (project) =>
          project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          project.technologies?.some((tech) =>
            tech.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (selectedTech !== "all") {
      filtered = filtered.filter((project) =>
        project.technologies?.includes(selectedTech)
      );
    }

    setFilteredProjects(filtered);
  }, [searchTerm, selectedTech, projects]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 pb-16 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <section ref={portfolioRef} className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={portfolioInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              My Portfolio
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              A showcase of my projects, highlighting skills and experience in
              web development.
            </p>
          </motion.div>

          {/* Search & Filter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={portfolioInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 space-y-4"
          >
            <div className="relative max-w-md mx-auto">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              {allTechnologies.map((tech) => (
                <button
                  key={tech}
                  onClick={() => setSelectedTech(tech)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                    selectedTech === tech
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/20"
                  }`}
                >
                  {tech}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-gray-500 text-lg">
                No projects found matching your criteria.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 md:gap-7 lg:grid-cols-3 lg:gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project._id || index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={portfolioInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex h-full min-h-0"
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const ProjectCard = ({ project }) => {
  const thumb = project.images?.[0]?.url;
  const imageSrc = thumb ? getImageUrl(thumb) : null;

  return (
    <article className="group flex h-full w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/[0.03] transition-[box-shadow,border-color,transform] hover:border-gray-300 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:ring-white/[0.04] dark:hover:border-gray-600">
      {imageSrc ? (
        <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-900/80">
          <img
            src={imageSrc}
            alt={project.title || "Project"}
            className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-950/40 via-transparent to-transparent"
            aria-hidden
          />
          <div className="absolute bottom-3 right-3 flex gap-2">
            {project.liveUrl ? (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/25 bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm transition hover:bg-white hover:text-primary-600 dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-100 dark:hover:text-primary-400"
                aria-label="Live site"
              >
                <FiExternalLink size={16} />
              </a>
            ) : null}
            {project.githubUrl ? (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/25 bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm transition hover:bg-white hover:text-primary-600 dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-100 dark:hover:text-primary-400"
                aria-label="Source on GitHub"
              >
                <FiGithub size={16} />
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex aspect-[16/10] w-full shrink-0 items-center justify-center border-b border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 dark:border-gray-700 dark:from-gray-900 dark:to-gray-950">
          <FiCode className="text-gray-300 dark:text-gray-600" size={32} strokeWidth={1.15} aria-hidden />
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col p-5 md:p-6">
        <h3 className="text-lg font-semibold leading-snug tracking-tight text-gray-900 transition group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400 md:text-xl">
          <Link to={`/project/${project._id}`} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800">
            {project.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {project.description}
        </p>

        {project.technologies?.length ? (
          <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Technologies">
            {project.technologies.slice(0, 5).map((tech, idx) => (
              <li key={idx}>
                <span className="inline-block rounded-md border border-gray-200/90 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-900/50 dark:text-gray-300">
                  {tech}
                </span>
              </li>
            ))}
            {project.technologies.length > 5 ? (
              <li>
                <span className="px-1 font-mono text-[10px] text-gray-500">+{project.technologies.length - 5}</span>
              </li>
            ) : null}
          </ul>
        ) : null}

        <Link
          to={`/project/${project._id}`}
          className="mt-5 inline-flex items-center gap-2 border-t border-gray-100 pt-4 text-sm font-semibold text-primary-600 transition hover:text-primary-700 dark:border-gray-700/80 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Case study
          <FiArrowRight className="transition-transform group-hover:translate-x-0.5" size={16} aria-hidden />
        </Link>
      </div>
    </article>
  );
};

export default Portfolio;
